import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { getFingerprint } from '@/utils/fingerprint';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    // Check if user is logged in on mount
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, []);

  // Setup axios interceptor for handling 401 errors
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Session expired or invalid token
          console.log('Session expired, redirecting to login...');
          logout();
          // Redirect to login page
          window.location.href = '/game/login';
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const register = async (email, password, fullName, country = null) => {
    const referralSlug = (() => {
      try {
        const slug = localStorage.getItem('referral_slug');
        const expiresAt = parseInt(localStorage.getItem('referral_expires_at') || '0', 10);
        if (slug && expiresAt && Date.now() < expiresAt) return slug;
      } catch (e) { /* ignore */ }
      return null;
    })();
    const payload = {
      email,
      password,
      full_name: fullName,
    };
    if (referralSlug) payload.referral_slug = referralSlug;
    if (country) payload.country = country;
    try {
      const fp = await getFingerprint();
      if (fp) payload.device_fingerprint = fp;
    } catch (e) { /* fingerprint is best-effort */ }
    const response = await axios.post(`${API}/auth/register`, payload);
    
    // Registration now requires email verification
    // No token is returned, user must verify email first
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const isAuthenticated = () => {
    return !!user;
  };

  const hasRole = (roles) => {
    if (!user) return false;
    if (Array.isArray(roles)) {
      return roles.includes(user.role);
    }
    return user.role === roles;
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    isAuthenticated,
    hasRole,
    getAuthHeader,
    token
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};